import { CDN } from "../../CDN.js";
import { UiVideoPlayer } from "./VideoPlayer.js";
import { ImageUtils } from "./ImageUtils.js";
import { VectorGraphics } from "./VectorGraphics.js";
import { PopupPagination } from "../../map/popup/PopupPagination.js";
import { MediaCaption, CloseButton, ShareButton, MetadataButton, ZoomText } from "./LightboxUtils.js";
import { EventObject } from "../../events/EventObject.js";
import { MediaDataService } from "./MediaGalleryUtils.js";
import { VideoPlayerUtils } from "./VideoPlayerUtils.js";
import { DeviceProperties, ElementProperties } from "./HelperTools.js";
export { Lightbox }

/**
 * Lightbox class to build a media lightbox using the core functionalities of the photoswipe lightbox. However, the pswp UI is replaced by own UI design, thus own DOM structure and elements (e.g., buttons, text) 
 * @memberof vef.ui.media
 * @extends EventObject
 * @author ckraemme <ckraemme@awi.de>
 */
class Lightbox extends EventObject {
    constructor(target, featureCollection, slideOnStart = undefined, metadataTemplates) {
        super({
            "lightbox_open": [], // [1]
            "lightbox_close": [],// [1]
            //[1]: required for enabling/disabling scrollbars
        });

        this.target = target;
        this.featureCollection = featureCollection;
        this.featureList = featureCollection.features;
        this.slideOnStart = slideOnStart;
        this.metadataTemplates = metadataTemplates;
    }

    init() {
        if (this.slideOnStart != null) {
            const hideThumbnailOpeningAnimation = true;
            this._build(hideThumbnailOpeningAnimation);
        }

        const gallery = this.target.querySelector('.pswp-gallery');
        if (gallery) {
            gallery.addEventListener('click', (e) => {
                this.slideOnStart = this._getIndexOfItemInGallery(e.target);
                if (Number.isInteger(this.slideOnStart)) {
                    e.preventDefault();
                    this._build();
                }
            })
        }
    }

    _opening() {
        this.lightboxWrapper.classList.remove('state-before-opening');
        this.lightboxWrapper.classList.add('state-opening');
        this._initLightboxHistoryEntry();
    }

    _open() {
        this.lightboxWrapper.classList.remove('state-opening');
    }

    _closing() {
        this.lightboxWrapper.classList.add('state-closing');
        this.shareButton.closeContent();
        this._disableClosingAnimationBasedOnItemVisibility();
        this._removeLightboxHistoryEntry();
    }

    _closed() {
        this.lightboxWrapper.remove();
        this.pswpLightbox = null;
        this.lightboxWrapper.classList.remove('state-closing');
        this.lightboxWrapper = null;
    }

    /**
     * On touch screens, allow the user to close the lightbox using the browser's back button (i.e. swipe back)
     * @private
     */
    _initLightboxHistoryEntry() {
        if (DeviceProperties.isTouchDevice()) {
            if (history.state?.name != "Lightbox")
                history.pushState({ name: "Lightbox" }, null, document.URL);

            this.historyStateMethod = this._removeLightboxHistoryEntry.bind(this);
            window.addEventListener('popstate', this.historyStateMethod);
        }
    }

    _removeLightboxHistoryEntry() {
        if (DeviceProperties.isTouchDevice()) {
            window.removeEventListener('popstate', this.historyStateMethod);
            if (history.state?.name == "Lightbox")
                history.back();
            this.pswpLightbox.lightbox.pswp?.close();
        }
    }

    /**
     * Disable lightbox closing animation if the gallery item is not in the viewport
     * @private
     */
    _disableClosingAnimationBasedOnItemVisibility() {
        const index = this.pswpLightbox.lightbox.pswp.currIndex;
        const placeholderElement = document.querySelectorAll('.pswp-gallery__item')[index];
        const deviation = 1.0;
        const isInViewport = ElementProperties.isInViewport(placeholderElement, deviation);
        if (!isInViewport)
            this.pswpLightbox.lightbox.pswp.options.showHideAnimationType = 'none';
    }

    _getIndexOfItemInGallery(itemNode) {
        const galleryItems = document.querySelectorAll('.pswp-gallery__item');
        let itemIndex;
        for (let index = 0; index < galleryItems.length; index++) {
            const item = galleryItems[index];
            if (item.contains(itemNode)) {
                itemIndex = index;
                break
            }
        }
        return itemIndex
    }

    /**
     * Set the height of the lightbox to window.innerHeight to prevent overflowing
     * content below the address bar in mobile browsers
     * @private
     */
    _setLightboxResizeListener() {
        this._resetLightboxResizeListener();
        this._resizeListener = () => {
            this.lightboxWrapper.style.height = window.innerHeight + "px";
        };
        window.addEventListener("resize", this._resizeListener);
        this._resizeListener();
    }

    _resetLightboxResizeListener() {
        if (this._resizeListener) {
            window.removeEventListener("resize", this._resizeListener);
        }
    }

    _build(hideThumbnailOpeningAnimation = false) {
        this._initDOM();
        this.pswpLightbox = new PhotoswipeLightbox(this.target.querySelector('.pswp-lightbox'), this.featureCollection, this.metadataTemplates, this.slideOnStart, hideThumbnailOpeningAnimation);
        this._handlePswpEvents();
        this._setLightboxResizeListener();
        this.fire("lightbox_open");
    }

    _initDOM() {
        this.lightboxWrapper = document.createElement('div');
        this.lightboxWrapper.classList.add(...['vef-ui', 'custom-lightbox', 'state-before-opening']);
        this._initDOMHeader();
        this._initDOMContent();
        this._initDOMPagination();
        this.target.append(this.lightboxWrapper);
    }

    _initDOMHeader() {
        let lightboxHeader = document.createElement('div');
        lightboxHeader.className = 'vef-ui lightbox-header';

        const headerLeft = document.createElement('div');
        headerLeft.classList.add('header-left');
        this.metadataButton = new MetadataButton(headerLeft);

        const headerRight = document.createElement('div');
        headerRight.classList.add('header-right');

        this.zoomText = new ZoomText(headerRight);
        this.shareButton = new ShareButton(headerRight);
        this.closeButton = new CloseButton(headerRight);

        lightboxHeader.append(headerLeft);
        lightboxHeader.append(headerRight);
        this.lightboxWrapper.prepend(lightboxHeader);
    }

    _initDOMContent() {
        const pswpLightbox = document.createElement('div');
        pswpLightbox.classList.add(...['vef-ui', 'lightbox-content', 'pswp-lightbox']);
        this.lightboxWrapper.append(pswpLightbox);
        this.shareButton.createBox(pswpLightbox);
        PswpLightboxEvents._preventOnShareBox(this.shareButton.contentBoxElement);
    }

    _initDOMPagination() {
        const lightboxFooter = document.createElement('div');
        lightboxFooter.classList.add(...['vef-ui', 'lightbox-footer']);
        this.pagination = new PopupPagination(lightboxFooter, { pageCount: this.featureList.length });
        this.lightboxWrapper.append(lightboxFooter);
    }

    _handlePswpEvents() {

        this.pswpLightbox.on('pswp_lightbox_opening', () => {
            this._opening();
        })

        this.pswpLightbox.on('pswp_lightbox_open', () => {
            this._open();
        })

        this.pswpLightbox.on('pswp_lightbox_ready', () => {
            const lightbox = this.pswpLightbox.lightbox;
            this.pagination.on('next', () => { lightbox.pswp.next() });
            this.pagination.on('previous', () => { lightbox.pswp.prev() });
            this.closeButton.onClick(() => { lightbox.pswp.close() })
        })

        this.pswpLightbox.on("pswp_lightbox_slide_change", (index) => {

            const getSharedLink = function (featureList, index) {
                let feature = featureList[index];
                if (Array.isArray(feature)) feature = feature[0];
                const shareID = feature['properties#harmonized']['persistent_identifier'];
                const searchParams = new URLSearchParams(window.location.search)
                searchParams.set("shareID", shareID);
                return window.location.origin + window.location.pathname + '?' + decodeURIComponent(searchParams.toString());
            }
            const sharedLink = getSharedLink(this.featureList, index);
            this.shareButton.setLink(sharedLink);

            let feature = this.featureCollection.features[index];
            if (feature instanceof Array)
                feature = feature[0];
            const layerName = feature.id.match(/(\D+)\./)[1];
            const caption = MediaCaption.getCaption(this.featureCollection, index, this.metadataTemplates[layerName]);
            const captionTarget = document.querySelector('.lightbox-content');
            this.metadataButton.setContent(caption, captionTarget);
            this.pagination.setPage(index + 1);
        })

        this.pswpLightbox.on("pswp_lightbox_zoom", ([zoomLevel, showZoomLevel]) => {
            this.zoomText.isContentVisible = showZoomLevel;
            this.zoomText.setZoom(zoomLevel);
        })

        this.pswpLightbox.on('pswp_lightbox_close', () => {
            this.fire("lightbox_close");
            this._resetLightboxResizeListener();
            this._closing();
        })

        this.pswpLightbox.on('pswp_lightbox_destroy', () => {
            this._closed();
        })
    }
}



/**
 * Project related photoswipe lightbox class
 * @memberof vef.ui.media
 * @extends EventObject
 * @author ckraemme <ckraemme@awi.de>
 * @private
*/
class PhotoswipeLightbox extends EventObject {

    /**
     * Set up the photoswipe lightbox with project related configurations and events
     * @param {string/HTMLElement} target - target HTML element, serves as anchor for the lightbox
     * @param {object} featureCollection - dictionary containing data information. Structure of the dictionary is based on the harmonized response of the maps.awi.de web feature service
     * @param {object} metadataTemplates - dictionary with metadata templates based on layer key
     * @param {Number|undefined} slideOnStart - open lightbox after loading and show a specific slide (starts at zero). Is skipped when undefined
     * @param {Boolean} hideThumbnailOpeningAnimation - if true, the pswp easing animation is hidden when the lightbox opens
     */
    constructor(target, featureCollection, metadataTemplates, slideOnStart = undefined, hideThumbnailOpeningAnimation = false) {
        super({
            "pswp_lightbox_opening": [],
            "pswp_lightbox_ready": [],
            "pswp_lightbox_open": [],
            "pswp_lightbox_slide_change": [],
            "pswp_lightbox_zoom": [],
            "pswp_lightbox_close": [],
            "pswp_lightbox_destroy": [],
        });

        this.target = target;
        this.featureCollection = featureCollection;
        this.featureList = featureCollection.features;
        this.metadataTemplates = metadataTemplates;
        this.slideOnStart = slideOnStart;
        this.hideThumbnailOpeningAnimation = hideThumbnailOpeningAnimation;

        this.mobileLayoutWidth = 1280;
        this.photoswipeItemPaddingNonMobile = {
            top: 80,
            bottom: 80,
            left: 40,
            right: 40
        }
        this.photoswipeItemPaddingMobile = {
            top: 40,
            bottom: 40,
            left: 0,
            right: 0
        }

        this._initModules().then(() => {
            this._setupPhotoswipeLightbox();
            this._initCaptionObserver();
        })
    }


    /**
     * Load dependencies asynchronously
     * @private
     * @returns {Promise} Promise that is resolved when loaded
     */
    _initModules() {
        return new Promise(resolve => {
            Promise
                .all([
                    CDN.import("Photoswipe"),
                    CDN.import("PhotoswipeLightbox"),
                    CDN.import("PhotoSwipeDynamicCaption")
                ])
                .then(modules => {
                    this.modules = {
                        Photoswipe: modules[0],
                        PhotoswipeLightbox: modules[1].default,
                        PhotoSwipeDynamicCaption: modules[2].default,
                    }
                    resolve();
                })
        });
    }


    /**
     * Set up the lightbox with initial configuration
     * @private
     */
    _setupPhotoswipeLightbox() {

        var options = {
            gallery: '.pswp-gallery',
            childSelector: '.pswp-gallery__item',
            appendToEl: this.target,
            pswpModule: () => this.modules.Photoswipe,
            wheelToZoom: true,
            indexIndicatorSep: ' of ', // only if counter: true
            paddingFn: (viewportSize, itemData, index) => {
                if (viewportSize.x < this.mobileLayoutWidth)
                    return this.photoswipeItemPaddingMobile
                else
                    return this.photoswipeItemPaddingNonMobile
            },
            bgOpacity: 1.0,
            bgClickAction: false,
            initialZoomLevel: 'fit',
            secondaryZoomLevel: (zoomLevelObject) => {
                //Secondary zoom level is fill, but minimum 2 of fit
                return Math.max(2, zoomLevelObject.fill / zoomLevelObject.fit) * zoomLevelObject.fit
            },
            maxZoomLevel: 4.0,
            closeOnVerticalDrag: true,
            pinchToClose: false,
            zoom: false,
            tapAction: false,
            // disable buttons:
            arrowKeys: true,
            arrowPrev: false,
            arrowNext: false,
            zoom: false,
            close: false,
            counter: false,
        };

        const lightbox = new this.modules.PhotoswipeLightbox(options);
        this.lightbox = lightbox;

        lightbox.on('beforeOpen', () => {
            this.fire('pswp_lightbox_opening');
        });

        lightbox.on('afterInit', () => {
            this.fire('pswp_lightbox_ready');
        })

        lightbox.on('openingAnimationEnd', () => {
            this.fire('pswp_lightbox_open')
        });

        lightbox.on('contentActivate', ({ content }) => {
            this.fire('pswp_lightbox_slide_change', content.index)
        });

        lightbox.on('close', () => {
            this.fire('pswp_lightbox_close');
        })

        lightbox.on('destroy', () => {
            this.fire('pswp_lightbox_destroy');
        })

        lightbox.addFilter('numItems', (numItems, dataSource) => {
            return this.featureList.length
        });

        lightbox.addFilter('placeholderSrc', (placeholderSrc, slide) => {
            return placeholderSrc || slide.data.msrc || false;
        });

        lightbox.addFilter('thumbEl', (thumbnail, itemData, index) => {
            //modify the thumbnail element from which opening zoom animation starts or ends
            if (this.hideThumbnailOpeningAnimation)
                lightbox.pswp.options.showAnimationDuration = 0;
            if (!thumbnail && itemData.msrc) {
                const newThumbnail = document.querySelectorAll('.pswp-gallery__item')[index];
                return newThumbnail;
            }
            return thumbnail
        });

        lightbox.on('contentLoadImage', ({ content, slide }) => {
            // remove flashing of lightbox image when pswp__img--placeholder has been removed, however rendering requires small amount of time
            const backgroundImage = content.data.msrc;
            if (content.type === 'image' && backgroundImage) {
                content.element.style.backgroundImage = `url('${backgroundImage}')`;
                content.element.style.backgroundSize = "cover";
            }
        });

        lightbox.addFilter('itemData', (itemData, index) => {
            if (index < this.featureList.length) {
                let feature = this.featureList[index];
                if (Array.isArray(feature)) feature = feature[0];
                const featurePropertiesHarmonized = feature["properties#harmonized"];
                let pswpData;
                if (featurePropertiesHarmonized["mime_type"].startsWith("image")) {

                    const imageSrcData = featurePropertiesHarmonized["photo_src"];
                    const imageSrcsetData = featurePropertiesHarmonized["photo_srcset"] || '';
                    let [imageSrc, imageSrcset] = ImageUtils.improveLightboxPerformance(imageSrcData, imageSrcsetData);

                    pswpData = {
                        customIdentifier: featurePropertiesHarmonized["persistent_identifier"],
                        height: featurePropertiesHarmonized["photo_height"] || MediaDataService.getFallbackDimension(),
                        src: imageSrc,
                        srcset: imageSrcset,
                        type: "image",//init and used by photoswipe, do not rename!
                        width: featurePropertiesHarmonized["photo_width"] || MediaDataService.getFallbackDimension(),
                    }

                    if (!itemData.msrc && imageSrcsetData) {
                        const placeholderSrc = ImageUtils.srcsetSelector(imageSrcsetData, 1)[0];
                        pswpData.msrc = placeholderSrc;
                    }
                }
                else if (featurePropertiesHarmonized["mime_type"].startsWith("video")) {
                    pswpData = {
                        customIdentifier: featurePropertiesHarmonized["persistent_identifier"],
                        customSrcCover: featurePropertiesHarmonized["cover_src"],
                        customSrcMedia: featurePropertiesHarmonized["video_src"],
                        customSrcPoster: VideoPlayerUtils.getPoster(),
                        customSrcThumbnail: featurePropertiesHarmonized["thumbnail_src"],
                        height: featurePropertiesHarmonized["video_height"] || MediaDataService.getFallbackDimension(),
                        type: "video",
                        width: featurePropertiesHarmonized["video_width"] || MediaDataService.getFallbackDimension(),
                    }
                }
                pswpData['h'] = pswpData['height'];
                pswpData['w'] = pswpData['width'];
                const itemDataNew = Object.assign({}, itemData, pswpData);
                return itemDataNew
            }
            else {
                console.error(`photoswipe gallery page index (${index}) out of input data range (${this.featureList.length})`)
            }
            return itemData
        });

        lightbox.on('contentLoad', (e) => {
            const { content } = e;
            if (content.type === 'video') {
                e.preventDefault();
                const videoContainer = document.createElement('div');
                videoContainer.className = 'pswp__video-container';
                PswpLightboxEvents._preventOnVideoContainer(videoContainer);

                let optionsPlayer = {
                    'media': content.data.customSrcMedia,
                    'thumbnail': content.data.customSrcThumbnail,
                    'poster': content.data.customSrcPoster,
                    'cover': content.data.customSrcCover,
                    'initPlayerWithKeyFocus': true
                }
                const player = new UiVideoPlayer(videoContainer, optionsPlayer);
                content.element = videoContainer;
            }
        });

        new this.modules.PhotoSwipeDynamicCaption(lightbox, {
            type: 'aside',
            captionContent: function (slide) {
                const caption = "placeholder";
                return caption
            }
        });

        lightbox.on("dynamicCaptionUpdateHTML", e => {
            const captionBox = e.captionElement;
            captionBox.innerHTML = "";
            let feature = this.featureCollection.features[e.slide.index];
            if (feature instanceof Array)
                feature = feature[0];
            const layerName = feature.id.match(/(\D+)\./)[1];
            const caption = MediaCaption.getCaption(this.featureCollection, e.slide.index, this.metadataTemplates[layerName]);
            captionBox.appendChild(caption);
        });

        lightbox.on('zoomPanUpdate', e => {
            const zoomLevel = pswp.currSlide.currZoomLevel;
            const showZoomLevel = pswp.currSlide.data.type == "image";
            this.fire("pswp_lightbox_zoom", [zoomLevel, showZoomLevel]);
        });

        lightbox.addFilter('contentErrorElement', (contentErrorElement, content) => {
            const imageElement = document.createElement('img');
            imageElement.src = VectorGraphics.errorImage('Photo');
            imageElement.style.width = "100%";
            imageElement.style.height = "100%";
            imageElement.style.top = "0px";
            imageElement.style.position = "absolute";
            return imageElement;
        });

        lightbox.init();
        if (this.slideOnStart != null) {
            lightbox.loadAndOpen(this.slideOnStart)
        }
    }


    /**
     * Observe with a mutation observer for new captions and extend the photoswipe caption plugin with additional functionality, e.g., include a caption resize observer for caption positioning and a event listeners for mouse events above the caption.
     * This function is required because the default caption plugin is intended to display text only, however more is needed in marine-data.
     * @private
     */
    _initCaptionObserver() {

        const captionResizeObserver = new ResizeObserver(entries => {
            //console.log('resize');
            entries.forEach(entry => {
                const captionBox = entry.target;

                const slideHeight = this.lightbox.pswp.container.offsetHeight;
                const captionHeightMax = slideHeight - (this.photoswipeItemPaddingNonMobile.top + this.photoswipeItemPaddingNonMobile.bottom)
                const mediaItemHeight = this.lightbox.pswp.currSlide.prevDisplayedHeight;
                const captionItemHeight = captionBox.offsetHeight;
                const captionScrollHeight = captionBox.scrollHeight;

                let captionBoxStyleTopMax = Infinity;
                let captionBoxStyleBottomMax = Infinity;

                if (mediaItemHeight < 0.5 * slideHeight && captionScrollHeight > mediaItemHeight) {
                    if (captionHeightMax > captionScrollHeight) {
                        captionBoxStyleTopMax = (captionHeightMax - captionScrollHeight) / 2 + this.photoswipeItemPaddingNonMobile.top;
                        captionBoxStyleBottomMax = (captionHeightMax - captionScrollHeight) / 2 + this.photoswipeItemPaddingNonMobile.bottom;
                    }
                    else {
                        captionBoxStyleTopMax = this.photoswipeItemPaddingNonMobile.top;
                        captionBoxStyleBottomMax = this.photoswipeItemPaddingNonMobile.bottom;
                    }
                }

                const captionBoxInitialStyleTop = parseInt(captionBox.style.top, 10);
                const captionBoxStyleTop = Math.min(captionBoxInitialStyleTop, captionBoxStyleTopMax);

                const captionBoxStyleBottom = Math.min(captionBoxInitialStyleTop, captionBoxStyleBottomMax);

                captionBox.style.top = captionBoxStyleTop + "px";
                captionBox.style.bottom = captionBoxStyleBottom + "px";

            });
        });

        const muObserver = new MutationObserver(function (mutations) {
            mutations.forEach((mutation) => {
                for (let i = 0; i < mutation.removedNodes.length; i++) {
                    let node = mutation.removedNodes[i]
                    if (typeof node.classList !== 'undefined' && node.classList.contains('pswp__dynamic-caption') == true) {
                        //console.log('closed')
                        captionResizeObserver.unobserve(node)
                    }
                }
                for (let i = 0; i < mutation.addedNodes.length; i++) {
                    let node = mutation.addedNodes[i]
                    if (typeof node.classList !== 'undefined' && node.classList.contains('pswp__dynamic-caption') == true) {
                        //console.log('init');
                        captionResizeObserver.observe(node);
                        PswpLightboxEvents._preventOnCaption(node);
                    }
                }
            })
        }.bind(this))

        muObserver.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: false,
            characterData: false
        })

    }
}


/**
 * By default, certain events are detected throughout the lightbox element and their default action is prevented.
 * However, this is not desired for custom lightbox elements, like share box element or caption element.
 * They must be selectable + clickable (='pointerdown') and scrollable (='wheel').
 * This class stops the propagation of some events that should not be fired at lightbox level.
 * @private
 */
class PswpLightboxEvents {
    static _preventOnCaption(captionElement) {
        const isNotTouchDevice = !DeviceProperties.isTouchDevice();
        if (isNotTouchDevice) {
            captionElement.addEventListener('pointerdown', (event) => { event.stopPropagation() });
            captionElement.addEventListener('wheel', (event) => { event.stopPropagation() }, { passive: true });
            captionElement.style.cssText += ';' + "-webkit-user-select: text; -moz-user-select: text; -ms-user-select: text; user-select: text;";
        }
    }

    static _preventOnShareBox(shareBoxElement) {
        shareBoxElement.addEventListener('focusin', e => { e.stopPropagation() })
        shareBoxElement.addEventListener('pointermove', e => { e.stopPropagation() })
    }

    static _preventOnVideoContainer(videoContainerElement) {
        videoContainerElement.addEventListener('pointerdown', (event) => { event.stopPropagation() });
        videoContainerElement.addEventListener('keydown', (event) => { event.stopPropagation() });
    }
}